import React, { useEffect, useRef, useState } from 'react';
import animationCache from '../utils/animationCache';
import './AnimatedLetter.css';

const AnimatedLetter = ({ letter, alphabetId, videoUrl, fallbackImageUrl = null, animatedPreviewUrl = null, onError, superpositionPourcentage = 0, superpositionType = 'normal', isFirstLetter = false, replayKey = 0, shouldPlayVideo = true, exportPhase = null, sizeScale = 1 }) => {
  const [state, setState] = useState('loading'); // loading, thumbnail, playing, played, error, fallback
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [videoDimensions, setVideoDimensions] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [videoDecodeBlocked, setVideoDecodeBlocked] = useState(false);
  const videoRef = useRef(null);
  const loadRunRef = useRef(0);
  const onErrorRef = useRef(onError);
  const lastHandledReplayKeyRef = useRef(replayKey);
  const activeReplayKeyRef = useRef(null);
  const prevShouldPlayRef = useRef(shouldPlayVideo);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    prevShouldPlayRef.current = shouldPlayVideo;
  }, [shouldPlayVideo]);

  useEffect(() => {
    setVideoDecodeBlocked(false);
  }, [letter, alphabetId, videoUrl]);

  useEffect(() => {
    let isMounted = true;

    const loadVideo = async () => {
      try {
        loadRunRef.current += 1;
        setState('loading');
        
        const cachedThumbnail = animationCache.getThumbnail(letter, alphabetId);
        
        if (cachedThumbnail) {
          setThumbnailUrl(cachedThumbnail);
          setState('thumbnail');
        } else if (fallbackImageUrl) {
          setThumbnailUrl(fallbackImageUrl);
          setState('thumbnail');
        }
        
        const video = await animationCache.getAnimation(letter, alphabetId, videoUrl);
        
        if (!isMounted) return;
        
        // Essayer d'obtenir les dimensions, mais ne pas bloquer si elles ne sont pas disponibles
        // Avec crossOrigin='anonymous', les dimensions peuvent ne pas être disponibles immédiatement
        let videoWidth = video.videoWidth || 0;
        let videoHeight = video.videoHeight || 0;
        
        // Si les dimensions ne sont pas disponibles, essayer de les obtenir de manière non-bloquante
        if (videoWidth === 0 || videoHeight === 0) {
          // Essayer une fois de plus après un court délai (non-bloquant)
          const checkDimensions = () => {
            if (video.videoWidth > 0 && video.videoHeight > 0 && isMounted) {
              setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
            }
          };
          
          if (video.readyState >= 1) {
            // Si les métadonnées sont déjà chargées, vérifier immédiatement
            checkDimensions();
          } else {
            // Sinon, attendre loadedmetadata de manière non-bloquante
            video.addEventListener('loadedmetadata', checkDimensions, { once: true });
            // Timeout de sécurité pour ne pas attendre indéfiniment
            setTimeout(() => {
              video.removeEventListener('loadedmetadata', checkDimensions);
              if (isMounted && video.videoWidth > 0 && video.videoHeight > 0) {
                setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
              }
            }, 500);
          }
        } else {
          // Dimensions disponibles immédiatement
          if (isMounted) {
            setVideoDimensions({ width: videoWidth, height: videoHeight });
          }
        }
        
        if (!cachedThumbnail) {
          const newThumbnail = animationCache.getThumbnail(letter, alphabetId);
          
          if (newThumbnail) {
            setThumbnailUrl(newThumbnail);
            setState('thumbnail');
          } else {
            // Pas de thumbnail disponible (probablement à cause de restrictions de sécurité)
            // Afficher directement la vidéo en pause à la première frame
            setShowVideo(true);
            setState('thumbnail'); // Utiliser 'thumbnail' pour indiquer qu'on affiche la vidéo comme thumbnail
          }
        }

        const hasImmediateDecodeBlockSignal = video.videoWidth === 0 || video.videoHeight === 0;
        if (shouldPlayVideo && (videoDecodeBlocked || hasImmediateDecodeBlockSignal) && animatedPreviewUrl) {
          setVideoDecodeBlocked(true);
          setThumbnailUrl(animatedPreviewUrl);
          setShowVideo(false);
          setState('playing');
        } else if (shouldPlayVideo) {
          // Si la lettre est déclenchée par la 2e frappe, passer en lecture
          // même sans cachedThumbnail (ex: fallbackImageUrl backend)
          setShowVideo(true);
          // Ne pas effacer thumbnailUrl : utilisé comme poster pour éviter le flash noir
          setState('playing');
        } else if (!cachedThumbnail && !shouldPlayVideo) {
          // Pas de thumbnail et pas de lecture : afficher la vidéo en pause
          setShowVideo(true);
          setState('thumbnail');
        } else if (cachedThumbnail && !shouldPlayVideo) {
          setState('thumbnail');
        }
      } catch (error) {
        console.error(`Erreur de chargement pour ${letter}:`, error);
        if (isMounted) {
          const fallbackThumbnail = animationCache.getThumbnail(letter, alphabetId);
          if (fallbackThumbnail) {
            setThumbnailUrl(fallbackThumbnail);
            setState('thumbnail');
          } else if (fallbackImageUrl) {
            setThumbnailUrl(fallbackImageUrl);
            setState('thumbnail');
          } else {
            setState('error');
          }
          if (onErrorRef.current) onErrorRef.current(letter, error);
        }
      }
    };

    loadVideo();
    return () => {
      isMounted = false;
    };
  }, [letter, alphabetId, videoUrl, fallbackImageUrl, animatedPreviewUrl, shouldPlayVideo]);

  // Contrôler la lecture de la vidéo (rendue par React)
  // Ne jouer la vidéo que si state === 'playing', pas si state === 'thumbnail'
  useEffect(() => {
    if (showVideo && videoRef.current && state === 'playing') {
      const video = videoRef.current;
      const handleEnded = () => setState('played');
      const handleError = (e) => {
        console.error('Erreur de lecture vidéo:', e);
        const fallbackThumbnail = animationCache.getThumbnail(letter, alphabetId);
        if (fallbackThumbnail) {
          setThumbnailUrl(fallbackThumbnail);
          setShowVideo(false);
          setState('thumbnail');
        } else if (fallbackImageUrl) {
          setThumbnailUrl(fallbackImageUrl);
          setShowVideo(false);
          setState('thumbnail');
        } else {
          setState('fallback');
        }
        if (onErrorRef.current) onErrorRef.current(letter, e);
      };
      video.addEventListener('ended', handleEnded);
      video.addEventListener('error', handleError);
      const tryPlay = (reason) => {
        return video.play().then(() => {
        }).catch((playError) => {
          throw playError;
        });
      };

      const onCanPlay = () => {
        tryPlay('canplay').catch(() => {});
      };
      const onLoadedData = () => {
        tryPlay('loadeddata').catch(() => {});
      };
      video.addEventListener('canplay', onCanPlay);
      video.addEventListener('loadeddata', onLoadedData);

      if (video.readyState >= 2) {
        tryPlay('immediate').catch((playError) => {
          console.error('Erreur de lecture:', playError);
          const fallbackThumbnail = animationCache.getThumbnail(letter, alphabetId);
          if (fallbackThumbnail) {
            setThumbnailUrl(fallbackThumbnail);
            setShowVideo(false);
            setState('thumbnail');
          } else if (fallbackImageUrl) {
            setThumbnailUrl(fallbackImageUrl);
            setShowVideo(false);
            setState('thumbnail');
          } else {
            setState('error');
          }
        });
      }

      video.addEventListener('play', () => {}, { once: true });

      return () => {
        video.removeEventListener('ended', handleEnded);
        video.removeEventListener('error', handleError);
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('loadeddata', onLoadedData);
        video.pause();
      };
    } else if (showVideo && videoRef.current && state === 'thumbnail') {
      // Si la vidéo est affichée comme thumbnail, s'assurer qu'elle reste en pause
      const video = videoRef.current;
      video.pause();
      // S'assurer qu'elle est à la première frame
      if (video.readyState >= 1) {
        video.currentTime = 0.001;
      } else {
        video.addEventListener('loadedmetadata', () => {
          video.currentTime = 0.001;
          video.pause();
        }, { once: true });
      }
    }
  }, [showVideo, state, letter, alphabetId]);

  // Replay : toutes les lettres rejouent (pas seulement celles avec shouldPlayVideo)
  useEffect(() => {
    if (replayKey > 0 && replayKey <= lastHandledReplayKeyRef.current) {
      return;
    }

    if (replayKey > 0) {
      lastHandledReplayKeyRef.current = replayKey;
      activeReplayKeyRef.current = replayKey;
      if (videoDecodeBlocked && animatedPreviewUrl) {
        const replayPreviewUrl = `${animatedPreviewUrl}${animatedPreviewUrl.includes('?') ? '&' : '?'}rk=${replayKey}`;
        setShowVideo(false);
        setThumbnailUrl(replayPreviewUrl);
        setState('playing');
      } else {
        setShowVideo(true);
        setThumbnailUrl(null);
        setState('playing');
      }
    }
  }, [replayKey, videoDecodeBlocked, animatedPreviewUrl, letter, alphabetId, fallbackImageUrl]);

  // Lecture effective quand la vidéo est rendue (après showVideo=true)
  useEffect(() => {
    const isActiveReplay = replayKey > 0 && activeReplayKeyRef.current === replayKey;
    if (!isActiveReplay) {
      return;
    }

    if (videoDecodeBlocked && animatedPreviewUrl) {
      return;
    }

    if (showVideo && videoRef.current) {
      const video = videoRef.current;
      video.currentTime = 0;
      if (exportPhase === 'intro') {
        video.pause();
        return;
      }
      video.play().catch((playError) => {
        console.error('Erreur de relecture:', playError);
        const fallbackThumbnail = animationCache.getThumbnail(letter, alphabetId);
        if (fallbackThumbnail) {
          setThumbnailUrl(fallbackThumbnail);
          setShowVideo(false);
          setState('thumbnail');
        } else if (fallbackImageUrl) {
          setThumbnailUrl(fallbackImageUrl);
          setShowVideo(false);
          setState('thumbnail');
        } else {
          setState('error');
        }
      });
    }
  }, [replayKey, showVideo, exportPhase, letter, alphabetId]);

  // Utiliser des dimensions par défaut si videoDimensions est null ou a des dimensions 0x0
  // (peut arriver avec des restrictions de sécurité)
  const baseDimensions = (videoDimensions && videoDimensions.width > 0 && videoDimensions.height > 0) 
    ? videoDimensions 
    : { width: 200, height: 200 };

  const clampedScale = typeof sizeScale === 'number' && sizeScale > 0 ? sizeScale : 1;
  const dimensions = {
    width: baseDimensions.width * clampedScale,
    height: baseDimensions.height * clampedScale,
  };
  
  const overlapMargin = superpositionPourcentage > 0 && !isFirstLetter
    ? `-${(dimensions.width * superpositionPourcentage) / 100}px`
    : '0';

  // Si la vidéo ne peut pas être lue ou qu'aucun fallback valable n'est disponible,
  // ne rien afficher (comportement demandé : ne rien montrer si la lettre n'a pas
  // de correspondance exploitable).
  if (state === 'error' || state === 'fallback') {
    return null;
  }

  return (
    <span 
      className={`animated-letter animated-letter-${state}`} 
      style={{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        marginLeft: overlapMargin,
        mixBlendMode: superpositionType,
        flexShrink: 0
      }}
    >
      {state === 'loading' && !thumbnailUrl && videoDimensions && (
        <span className="animated-letter-placeholder">{letter}</span>
      )}
      {state === 'error' && (
        <span className="animated-letter-error" title={`Erreur: ${letter}`}>{letter}</span>
      )}
      {thumbnailUrl && !showVideo && (
        <>
          <img
            src={thumbnailUrl}
            alt=""
            className="animated-letter-thumbnail"
            onLoad={() => {}}
            onError={() => {
              setThumbnailUrl(null);
              setShowVideo(false);
              setState('fallback');
            }}
          />
        </>
      )}
      {showVideo && videoUrl && (
        <>
          <video
            ref={videoRef}
            src={videoUrl}
            poster={thumbnailUrl || undefined}
            muted
            playsInline
            loop={false}
            preload="metadata"
            className="animated-letter-video"
            style={state === 'thumbnail' ? { pointerEvents: 'none' } : {}}
            onLoadedMetadata={() => {
              // Si on affiche la vidéo comme thumbnail, s'assurer qu'elle est à la première frame et en pause
              if (state === 'thumbnail' && videoRef.current) {
                const video = videoRef.current;
                video.pause();
                const canSeekSafely = video.videoWidth > 0 && video.videoHeight > 0;
                if (canSeekSafely) {
                  video.currentTime = 0.001;
                }
              }
            }}
            onLoadedData={() => {
              // S'assurer que la vidéo reste en pause si elle est utilisée comme thumbnail
              if (state === 'thumbnail' && videoRef.current) {
                const video = videoRef.current;
                video.pause();
                const hasRenderableFrame = video.videoWidth > 0 && video.videoHeight > 0;
                if (!hasRenderableFrame) {
                  setVideoDecodeBlocked(true);
                  setShowVideo(false);
                  if (fallbackImageUrl) {
                    setThumbnailUrl(fallbackImageUrl);
                    setState('thumbnail');
                  } else {
                    setState('fallback');
                  }
                  return;
                }
              }
            }}
            onEnded={() => setState('played')}
          onError={(e) => {
            const fallbackThumbnail = animationCache.getThumbnail(letter, alphabetId);
            if (fallbackThumbnail) {
              setThumbnailUrl(fallbackThumbnail);
              setShowVideo(false);
              setState('thumbnail');
            } else if (fallbackImageUrl) {
              setThumbnailUrl(fallbackImageUrl);
              setShowVideo(false);
              setState('thumbnail');
            } else {
              setState('error');
            }
            if (onErrorRef.current) onErrorRef.current(letter, e);
          }}
        />
        </>
      )}
    </span>
  );
};

export default AnimatedLetter;
